import { ICameraVideoTrack, IMicrophoneAudioTrack } from "agora-rtc-sdk-ng";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAgora } from "../../helpers/agoraProvider";
import { USER_TYPES } from "../../helpers/constant";
import { IAgoraTrack } from "../../helpers/types";
import { StoreState } from "../../redux/store";
import { socket } from "../../socket";
import agoraUtil from "../../utls/AgoraUtil";
import AgoraPlayerComponent from "../AgoraPlayerComponent";
import LiveJoinWaitComponent from "../LiveJoinWaitComponent";

type LocalTracks = {
  videoTrack: ICameraVideoTrack | null;
  audioTrack: IMicrophoneAudioTrack | null;
};

type props = {
  onStatusChanged: Function;
  handleDuration: Function;
  token: string;
  streamingTime: null | number;
  landscape?: boolean;
};

const STREAM_START_TIME_KEY = "stream_start_time_";

const getStreamStartTime = (streamId: string): number | null => {
  const stored = localStorage.getItem(STREAM_START_TIME_KEY + streamId);
  return stored ? parseInt(stored, 10) : null;
};

const setStreamStartTime = (streamId: string, startTime: number) => {
  localStorage.setItem(STREAM_START_TIME_KEY + streamId, startTime.toString());
};

const clearStreamStartTime = (streamId: string) => {
  localStorage.removeItem(STREAM_START_TIME_KEY + streamId);
};

const LivePublisherComponent = React.forwardRef(
  ({ onStatusChanged, handleDuration, token, streamingTime, landscape }: props, ref: React.Ref<any>) => {
    const { client } = useAgora();
    const live = useSelector((state: StoreState) => state.lives.item);
    //const token = useSelector((state: StoreState) => state.lives.token);
    const localTracks = useRef<LocalTracks>({ videoTrack: null, audioTrack: null });
    const currentFacingMode = useRef<"user" | "environment">("user");
    const clientRef = useRef<any>();
    const currentStreamIdRef = useRef<string | null>(null);
    let streamDurationIntervalRef = useRef<any>();
    const [tracks, setTracks] = useState<IAgoraTrack | null>(null);
    const [isStarted, setIsStarted] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const AGORA_APP_ID = process.env.REACT_APP_AGORA_APP_ID;
    const publish = async () => {
      if (!client || !live || !live._id || !AGORA_APP_ID || !live?.sessionId || !live.creator || !token) return;
      await client.join(AGORA_APP_ID, live.sessionId, token, live.creator._id);
      const [microphoneTrack, cameraTrack] = await agoraUtil.createLocalTracks(
        {},
        landscape
          ? {
              encoderConfig: {
                width: 1280, // 720p horizontal resolution — standard HD landscape
                height: 720, // 720p vertical resolution — standard HD landscape
                frameRate: 30, // 30fps is the sweet spot: smooth motion without doubling bandwidth vs 60fps
                bitrateMin: 1000, // floor bitrate to prevent quality collapse under network pressure
                bitrateMax: 3000, // Agora recommended ceiling for 720p30 (default 1500 causes artifacts on motion)
              },
            }
          : {}
      );
      await client.publish([microphoneTrack, cameraTrack]);

      setTracks({ tracks: [microphoneTrack, cameraTrack] });
      localTracks.current = { videoTrack: cameraTrack, audioTrack: microphoneTrack };
      socket.emit("public-stream/live", { id: live._id });
      onStatusChanged(true);
      setIsStarted(true);

      // Initialize or retrieve stream start time
      const streamId = live._id;
      currentStreamIdRef.current = streamId;
      let streamStartTime = getStreamStartTime(streamId);
      if (!streamStartTime) {
        // First time starting this stream - calculate start time based on backend streamingTime
        if (streamingTime !== null && streamingTime > 0) {
          // Stream was already running, calculate when it actually started
          streamStartTime = Date.now() - streamingTime * 1000;
        } else {
          // Brand new stream
          streamStartTime = Date.now();
        }
        setStreamStartTime(streamId, streamStartTime);
      }

      streamDurationIntervalRef.current && clearInterval(streamDurationIntervalRef.current);
      streamDurationIntervalRef.current = setInterval(() => {
        const currentStartTime = getStreamStartTime(streamId);
        if (currentStartTime) {
          const elapsedSeconds = Math.floor((Date.now() - currentStartTime) / 1000);
          handleDuration(elapsedSeconds);
        }
      }, 1000);
    };

    const leave = async () => {
      Object.keys(localTracks.current).forEach((trackName) => {
        if (localTracks.current[trackName as keyof LocalTracks]) {
          localTracks.current[trackName as keyof LocalTracks]?.stop();
          localTracks.current[trackName as keyof LocalTracks]?.close();
        }
      });
      localTracks.current = { videoTrack: null, audioTrack: null };
      setTracks(null);

      if (clientRef.current && clientRef.current.uid) {
        await clientRef.current.leave();
      }

      streamDurationIntervalRef.current && clearInterval(streamDurationIntervalRef.current);
    };

    const handleError = (error: string) => {
      setErrorMessage(error);
    };

    const mute = async () => {
      if (!localTracks?.current?.audioTrack) return;
      await localTracks.current.audioTrack.setMuted(true);

      return localTracks.current.audioTrack.muted;
    };

    const unmute = async () => {
      if (!localTracks?.current?.audioTrack) return;
      await localTracks.current.audioTrack.setMuted(false);

      return localTracks.current.audioTrack.muted;
    };

    const hasMultipleCameras = async (): Promise<boolean> => {
      const devices = await agoraUtil.getVideoDevices();
      return devices.length > 1;
    };

    const lockOrientation = async (lock: boolean) => {
      try {
        if (lock) {
          await window.screen.orientation.lock("portrait");
        } else {
          window.screen.orientation.unlock();
        }
      } catch (_) {
        // Orientation lock not supported (desktop browsers, etc.)
      }
    };

    const waitForPaint = () => new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

    const waitForTrackLive = (track: MediaStreamTrack, timeoutMs = 1000) =>
      new Promise<void>((resolve) => {
        if (track.readyState === "live" && !track.muted) {
          resolve();
          return;
        }
        const onUnmute = () => {
          track.removeEventListener("unmute", onUnmute);
          clearTimeout(timer);
          resolve();
        };
        const timer = setTimeout(() => {
          track.removeEventListener("unmute", onUnmute);
          resolve();
        }, timeoutMs);
        track.addEventListener("unmute", onUnmute);
      });

    const switchCamera = async () => {
      if (!localTracks.current.videoTrack) return;

      const videoEl = document.querySelector(".vjs-tech") as HTMLVideoElement;
      let freezeOverlay: HTMLCanvasElement | null = null;

      // Step 1: Capture last frame and overlay it BEFORE touching the camera
      if (videoEl && videoEl.videoWidth && videoEl.videoHeight) {
        freezeOverlay = document.createElement("canvas");
        freezeOverlay.width = videoEl.videoWidth;
        freezeOverlay.height = videoEl.videoHeight;
        const ctx = freezeOverlay.getContext("2d");
        if (ctx) {
          ctx.drawImage(videoEl, 0, 0);
          freezeOverlay.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;z-index:10;object-fit:cover;pointer-events:none;";
          freezeOverlay.style.transform = videoEl.style.transform || "";
          videoEl.parentElement?.appendChild(freezeOverlay);
        }
      }

      // Step 2: Wait until the browser has actually painted the overlay on screen
      await waitForPaint();

      // Step 3: Now switch the camera — the overlay hides the black frame
      const nextFacingMode = currentFacingMode.current === "user" ? "environment" : "user";
      await localTracks.current.videoTrack.setDevice({ facingMode: nextFacingMode });
      currentFacingMode.current = nextFacingMode;

      // Step 4: Swap the track in the existing MediaStream
      const newMediaTrack = localTracks.current.videoTrack.getMediaStreamTrack();
      if (videoEl && videoEl.srcObject instanceof MediaStream) {
        const stream = videoEl.srcObject;
        const oldVideoTracks = stream.getVideoTracks();
        oldVideoTracks.forEach((t) => stream.removeTrack(t));
        stream.addTrack(newMediaTrack);
      }

      // Step 5: Lock orientation for back camera, unlock for front camera
      const isForcedLandscape = landscape && window.matchMedia("(max-width: 768px) and (orientation: portrait)").matches;
      if (nextFacingMode === "environment") {
        if (!isForcedLandscape) await lockOrientation(true);
        // Back camera: counter-rotate only (no mirror) in forced landscape, or remove transform
        if (videoEl) videoEl.style.transform = isForcedLandscape ? "rotate(-90deg)" : "none";
      } else {
        if (!isForcedLandscape) await lockOrientation(false);
        // Front camera: counter-rotate + mirror in forced landscape, or default mirror
        if (videoEl) videoEl.style.transform = isForcedLandscape ? "rotate(-90deg) scaleX(-1)" : "rotateY(180deg)";
      }

      // Step 6: Wait for the new camera to produce frames, then remove the overlay
      if (freezeOverlay) {
        await waitForTrackLive(newMediaTrack);
        await waitForPaint();
        freezeOverlay.remove();
      }
    };

    useEffect(() => {
      clientRef.current = client;
    }, [client]);

    useEffect(() => {
      return () => {
        leave();
        // Unlock orientation on unmount
        lockOrientation(false);
      };
    }, []);

    const clearStreamDuration = () => {
      if (currentStreamIdRef.current) {
        clearStreamStartTime(currentStreamIdRef.current);
      }
    };

    React.useImperativeHandle(ref, () => ({
      publish,
      leave,
      handleError,
      mute,
      unmute,
      switchCamera,
      hasMultipleCameras,
      clearStreamDuration,
    }));
    return isStarted ? (
      <AgoraPlayerComponent
        isFromMobile={live?.isFromMobile!}
        tracks={tracks?.tracks || []}
        mirror={true}
        objectFit={live?.streamType === "broadcast" ? "contain" : "cover"}
      />
    ) : (
      <LiveJoinWaitComponent errorMessage={errorMessage} type={USER_TYPES.CREATOR} />
    );
  }
);

export default LivePublisherComponent;
